import { Injectable, computed, inject, signal } from '@angular/core';
import { resetApiErrorInterceptorState } from '@core/interceptors/api-error.interceptor';
import { resetAuthExpiredInterceptorState } from '@core/interceptors/auth-expired.interceptor';
import { AppStateResetService } from '@core/state/app-state-reset.service';
import { AuthStatus, AuthUser, LoginResult, RefreshTokenResult } from '../models/auth.model';

/** localStorage key under which the authenticated user is persisted. */
const AUTH_USER_STORAGE_KEY = 'veredis.auth.user';
/** localStorage key under which the access token is persisted (kept separate from the user). */
const AUTH_TOKEN_STORAGE_KEY = 'veredis.auth.token';

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private readonly appStateReset = inject(AppStateResetService);

  private readonly persistedUser = this.readPersistedUser();
  private readonly persistedToken = this.readPersistedToken();

  private readonly _status = signal<AuthStatus>(
    this.persistedUser && this.persistedToken ? 'authenticated' : 'idle',
  );
  private readonly _user = signal<AuthUser | null>(this.persistedUser);
  private readonly _token = signal<string | null>(this.persistedToken);
  private readonly _error = signal<string | null>(null);
  private readonly _challengeToken = signal<string | null>(null);
  private readonly _faceLoginEmail = signal<string | null>(null);
  private readonly _isForgotPasswordLoading = signal(false);
  private readonly _isResetPasswordLoading = signal(false);

  readonly status = this._status.asReadonly();
  readonly user = this._user.asReadonly();
  readonly token = this._token.asReadonly();
  readonly error = this._error.asReadonly();
  readonly challengeToken = this._challengeToken.asReadonly();
  readonly faceLoginEmail = this._faceLoginEmail.asReadonly();
  readonly isForgotPasswordLoading = this._isForgotPasswordLoading.asReadonly();
  readonly isResetPasswordLoading = this._isResetPasswordLoading.asReadonly();

  readonly isLoading = computed(() => this._status() === 'loading');
  readonly isAuthenticated = computed(() => this._status() === 'authenticated');

  constructor() {
    // The session token is cleared through the same registry as every other
    // feature's state, so a logout has a single entry point.
    this.appStateReset.register(() => this.reset());
  }

  setLoading(): void {
    this._status.set('loading');
    this._error.set(null);
  }

  setForgotPasswordLoading(loading: boolean): void {
    this._isForgotPasswordLoading.set(loading);
  }

  setResetPasswordLoading(loading: boolean): void {
    this._isResetPasswordLoading.set(loading);
  }

  setAuthenticated(result: LoginResult): void {
    this._status.set('authenticated');
    this._user.set(result.user);
    this._token.set(result.token);
    this._challengeToken.set(null);
    this._faceLoginEmail.set(null);
    this.persistUser(result.user);
    this.persistToken(result.token);
    resetAuthExpiredInterceptorState();
    resetApiErrorInterceptorState();
  }

  /**
   * Keeps the challenge the face step must answer. Held in memory only: the
   * user is not signed in yet, so nothing is persisted.
   */
  setLoginChallenge(challengeToken: string): void {
    this._status.set('idle');
    this._challengeToken.set(challengeToken);
    this._faceLoginEmail.set(null);
  }

  /**
   * Keeps the email the direct face login runs on (no password). Held in memory
   * only, like the challenge, and it replaces any pending one: the face step
   * answers exactly one of them.
   */
  setFaceLoginEmail(email: string): void {
    this._faceLoginEmail.set(email);
    this._challengeToken.set(null);
  }

  /** Persists a rotated token while keeping the authenticated user and status. */
  rotateToken(result: RefreshTokenResult): void {
    this._token.set(result.token);
    this.persistToken(result.token);
  }

  /** Replaces the signed-in user, keeping the token and status. */
  updateUser(user: AuthUser): void {
    this._user.set(user);
    this.persistUser(user);
  }

  setError(message: string): void {
    this._status.set('error');
    this._error.set(message);
  }

  reset(): void {
    this._status.set('idle');
    this._user.set(null);
    this._token.set(null);
    this._error.set(null);
    this._challengeToken.set(null);
    this._faceLoginEmail.set(null);
    this.clearPersisted();
  }

  private persistUser(user: AuthUser): void {
    try {
      localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
    } catch {
      // Storage unavailable (private mode / quota) — keep in-memory state only.
    }
  }

  private persistToken(token: string): void {
    try {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    } catch {
      // Storage unavailable — keep in-memory state only.
    }
  }

  private clearPersisted(): void {
    try {
      localStorage.removeItem(AUTH_USER_STORAGE_KEY);
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }

  private readPersistedUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem(AUTH_USER_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  }

  private readPersistedToken(): string | null {
    try {
      return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  }
}
